import { FC, useMemo, useEffect, Dispatch, SetStateAction } from "react";
import { InfoWindow, OverlayView } from "@react-google-maps/api";
import { ArrowRight, X } from "lucide-react";
import Address from "./Address";
import TaxiStatus from "./TaxiStatus";
import { Button } from "@/components/ui/button";
import { meters_to_km_velocity } from "@/utils/variables";
import { Vehicle } from "@/interface/entityInterface";

interface Props {
  data: Vehicle;
  selectedVehicleList: Vehicle | null;
  setSelectedVehicleList: Dispatch<SetStateAction<Vehicle | null>>;
  setOrderId: Dispatch<SetStateAction<number>>;
  setVehicleId: Dispatch<SetStateAction<number>>;
  setCenter: Dispatch<SetStateAction<{lat: number, lng: number}>>
}

const GoogleMarkerStatus: FC<Props> = ({
  data,
  selectedVehicleList,
  setSelectedVehicleList,
  setOrderId,
  setVehicleId,
  setCenter
}) => {
  const variables = useMemo(() => {
    const vars: Record<string, any> = {};
    data?.gpsData.variables?.forEach((variable: any) => {
      if (variable?.name) {
        vars[variable.name] = variable?.value || "False";
      }
    });
    return vars;
  }, [data]);

  const isSelected = selectedVehicleList?.id === data.id;
  const heading = data?.gpsData?.velocity?.heading || 0;

  const markerColor =
    data?.taxi_orders?.[0]?.status === "GOING_TO_PICKUP"
      ? "orange"
      : variables["Ignition"] === "True"
        ? variables["Pasagjer"] === "True" ||
          variables["SeatSensor"] === "True" ||
          variables["Prezenca"] === "True" ||
          variables["Taximeter"] === "True" ||
          variables["TaxiMeter"] === "True"
          ? "#ff5757"
          : "#07a619"
        : "gray";

  const nickname = data?.nickname || "00";
  const vehicleSpeed = useMemo(
    () =>
      Math.round(
        selectedVehicleList?.gpsData.velocity.groundSpeed *
          meters_to_km_velocity,
      ),
    [selectedVehicleList],
  );

  const goingToOrder = data.taxi_orders?.find(
    (order) => order.status === "GOING_TO_PICKUP",
  );

  return (
    <div className="relative">
      {selectedVehicleList?.id === data.id && (
        <InfoWindow
          options={{
            pixelOffset: new google.maps.Size(0, -20),
            minWidth: 280,
            headerDisabled: true,
          }}
          position={{
            lat: data.gpsData.position.latitude,
            lng: data.gpsData.position.longitude,
          }}
          onCloseClick={() => setSelectedVehicleList(null)}
        >
          <>
            <div className="absolute top-2.5 right-2">
              <Button
                onClick={() => setSelectedVehicleList(null)}
                className="w-6 h-6"
                variant="ghost"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center gap-x-2">
              <div className="flex items-center gap-x-4">
                <p className="text-[19px] font-bold">{data.nickname}</p>
                <TaxiStatus
                  data={selectedVehicleList.gpsData}
                  fuelTypes={selectedVehicleList.vehicle_tanks}
                />
              </div>
              <span
                className={
                  vehicleSpeed === 0
                    ? "text-gray-500"
                    : vehicleSpeed <= 100
                      ? "text-green-600"
                      : "text-red-600"
                }
              >
                {vehicleSpeed} km/h
              </span>
            </div>

            <div className="pt-3 flex items-center gap-x-2">
              <Address
                pickup_latitude={data.gpsData.position.latitude}
                pickup_longitude={data.gpsData.position.longitude}
                dropoff_latitude={0}
                dropoff_longitude={0}
              />
            </div>

            {goingToOrder?.id && (
               <div className="pt-3">
                <p>
                  <span className="font-bold">Going to</span>
                  <span
                    onClick={() => {
                      setSelectedVehicleList(null);
                      setVehicleId(0);
                      setOrderId(goingToOrder.id || 0);
                      setCenter({lat: goingToOrder.pickup_latitude, lng: goingToOrder.pickup_longitude});
                    }}
                    className="font-bold text-blue-500 cursor-pointer hover:underline"
                  >
                    {" "}
                    #{goingToOrder.id}
                  </span>
                </p>
              </div>
            )}
          </>
        </InfoWindow>
      )}
      <OverlayView
        position={{
          lat: data.gpsData.position.latitude,
          lng: data.gpsData.position.longitude,
        }}
        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      >
        <div
          onClick={() => {
            setSelectedVehicleList(data)
            setVehicleId(data.id)
          }}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            cursor: "pointer",
            transform: "translate(-50%, -100%)", // 👈 anchor correction: center horizontally, position above
          }}
        >
          <div
            style={{
              backgroundColor: markerColor,
              color: "#fff",
              padding: "4px 10px",
              borderRadius: 8,
              fontSize: 12,
              fontWeight: "bold",
              textAlign: "center",
              minWidth: 30,
              position: "relative",
              whiteSpace: "nowrap",
              marginBottom: "10px",
              display: "flex",
              alignItems: "center",
            }}
          >
            {nickname}{" "}
            <ArrowRight
              size={14}
              color="white"
              style={{
                marginLeft: "5xp",
                transform: `rotate(${heading - 90}deg)`,
              }}
            />
            <div
              style={{
                position: "absolute",
                bottom: -6,
                left: "50%",
                transform: "translateX(-50%)",
                width: 0,
                height: 0,
                borderLeft: "6px solid transparent",
                borderRight: "6px solid transparent",
                borderTop: `6px solid ${markerColor}`,
              }}
            />
          </div>

          <div
            style={{
              width: 25,
              height: 25,
              borderRadius: "50%",
              backgroundColor: markerColor,
              border: isSelected ? "2px solid white" : "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transform: `rotate(${heading - 90}deg)`,
              boxShadow: "0 0 3px rgba(0,0,0,0.3)",
            }}
          >
            <ArrowRight size={14} color="white" />
          </div>
        </div>
      </OverlayView>
    </div>
  );
};

export default GoogleMarkerStatus;
